/* @flow */
import * as React from 'react';
import { withTranslation } from 'react-i18next';
import { includes } from 'lodash';

import { capitalizeWords, abbreviateNumber, fuzzyMatch } from '../../utils/string';
import Checkbox from '../Checkbox';
import InputText from '../InputText';
import styles from './styles.module.scss';

import type { Bucket, Category, AggregationKey, Translate } from 'types';

type Props = {
  name: AggregationKey,
  label: string,
  categories?: Category[],
  buckets: Bucket[],
  selectedBuckets: $PropertType<Bucket, 'key'>[],
  onFilterToggle: (AggregationKey, $PropertType<Bucket, 'key'>) => mixed,
  onAllFiltersToggle?: AggregationKey => mixed,
  showAll?: boolean,
  canSearchFilters?: boolean,
  searchText?: string,
  onSearchTextChange?: (newText: string) => any,
  onLoadMore?: AggregationKey => mixed,
  t: Translate,
};

type State = {
  limit: number,
};

class Aggregation extends React.Component<Props, State> {
  state = {
    limit: 10,
  };

  render() {
    const {
      name,
      label,
      categories = [],
      buckets,
      selectedBuckets,
      onFilterToggle,
      onAllFiltersToggle,
      showAll = false || !!this.props.onLoadMore,
      canSearchFilters = false,
      searchText = '',
      onSearchTextChange,
      onLoadMore,
      t,
    } = this.props;
    const { limit } = this.state;
    const isFiltering = searchText.trim().length > 0;
    const orderedBuckets =
      categories.length > 0
        ? categories
            .map(category => buckets.find(bucket => bucket.key === category.id))
            .filter(bucket => bucket != null)
        : buckets;
    const filteredBuckets = orderedBuckets.filter(bucket => {
      const category = categories.find(category => category.id === bucket.key);
      if (name === 'categories' && category == null) {
        return false;
      }
      if (!isFiltering) {
        return true;
      }
      let label = bucket.key;
      if (name === 'categories') {
        //$FlowFixMe
        label = (category && category.descrizione) || label;
      }
      return fuzzyMatch(label, searchText);
    });
    if (filteredBuckets.length === 0 && !isFiltering) {
      return null;
    }
    const isAllChecked =
      filteredBuckets.length > 0 && filteredBuckets.every(b => includes(selectedBuckets, b.key));
    const canShowMore = !showAll && filteredBuckets.length > limit;
    return (
      <div className={styles.aggregation}>
        <div className={styles.aggregationLabel}>
          <Checkbox
            onChange={() => onAllFiltersToggle && onAllFiltersToggle(name)}
            small={true}
            value={undefined}
            checked={isAllChecked}
          />
          <p>{label}</p>
          {canSearchFilters && (
            <InputText
              placeholder={t('SEARCH')}
              onChange={({ target: { value } }) =>
                typeof value === 'string' && onSearchTextChange && onSearchTextChange(value)
              }
              value={searchText}
            />
          )}
        </div>
        <div className={styles.aggregationBuckets}>
          {filteredBuckets.slice(0, showAll ? filteredBuckets.length : limit).map(bucket => {
            const checked = includes(selectedBuckets, bucket.key);
            let label;
            const category = categories.find(category => category.id === bucket.key);
            switch (name) {
              case 'categories':
                label =
                  bucket.key === -1 || bucket.key === '-1' // NB possible elastic bug on missing values
                    ? t('NO_VALUE')
                    : //$FlowFixMe
                      category.description;
                break;
              case 'tags':
                label = bucket.key === '__MISSING__' ? t('NO_VALUE') : capitalizeWords(bucket.key);
                break;
              case 'years':
                label =
                  bucket.key === -1 || bucket.key === '-1' // NB possible elastic bug
                    ? t('NO_VALUE')
                    : bucket.key;
                break;
              default:
                label = bucket.key === '__MISSING__' ? t('NO_VALUE') : bucket.key;
                break;
            }
            return (
              <div
                key={bucket.key}
                className={styles.aggregationBucket}
                onClick={e => {
                  onFilterToggle && onFilterToggle(name, bucket.key);
                  e.preventDefault();
                }}
              >
                <Checkbox
                  onChange={() => null} // TODO richiesto per eliminare warning a runtime
                  small={true}
                  value={bucket.key}
                  label={label}
                  labelAfter={true}
                  checked={checked}
                />
                <div className={styles.aggregationBucketCount}>
                  {abbreviateNumber(bucket.count)}
                </div>
              </div>
            );
          })}
        </div>
        {canShowMore ? (
          <div
            className={styles.aggregationShowMore}
            onClick={() => {
              this.setState({ limit: limit + 10 });
            }}
          >
            {t('SHOW_MORE')}
          </div>
        ) : null}
        {onLoadMore && (
          <div className={styles.aggregationShowMore} onClick={() => onLoadMore(name)}>
            {t('SHOW_MORE')}
          </div>
        )}
      </div>
    );
  }
}

export default withTranslation()(Aggregation);
